// 数据浏览页面
// 提供传统的表格数据浏览功能，支持筛选、排序、搜索、导出
import React, { useState, useMemo } from 'react';
import {
  Card,
  Button,
  Space,

  Row,
  Col,
  Typography,
  Tag,
  Tooltip,
  Dropdown,
  Menu
} from 'antd';
import {
  DownloadOutlined,
  SettingOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { DEFAULT_VISIBLE_COLUMNS } from '../config/field-mapping';
import { FieldMappingUtils } from '../utils/field-mapping-utils';
import DataTable from '../components/browse/DataTable';
import SearchBar from '../components/browse/SearchBar';
import ColumnSelector from '../components/browse/ColumnSelector';
import useBrowseData from '../hooks/useBrowseData';
import type { SystemInfo } from '../types/api';
import type { DataTableColumn } from '../components/browse/DataTable';

const { Title, Text } = Typography;

interface BrowsePageProps {
  systemInfo?: SystemInfo | null;
}

const BrowsePage: React.FC<BrowsePageProps> = ({ systemInfo }) => {
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE_COLUMNS);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [columnSelectorVisible, setColumnSelectorVisible] = useState(false);
  const [tableDensity, setTableDensity] = useState<'compact' | 'default' | 'comfortable'>('default');

  // 使用数据浏览Hook
  const {
    data,
    loading,
    error,
    pagination,
    selectedRowKeys,
    refresh,
    setPage,
    setFilters,
    setSort,
    setSearch,
    clearFilters,
    clearSelection,
    setSelectedRows,
    exportData
  } = useBrowseData({
    initialPageSize: 20,
    autoLoad: true,
    cacheEnabled: true
  });

  // 构建表格列配置
  const columns: DataTableColumn[] = useMemo(() => {
    return FieldMappingUtils.getVisibleFields(visibleColumns).map(fieldConfig => ({
      key: fieldConfig.key,
      title: fieldConfig.label,
      dataIndex: fieldConfig.key,
      width: fieldConfig.width,
      align: (fieldConfig.type === 'number' || fieldConfig.type === 'currency' || fieldConfig.type === 'percentage') ? 'right' : 'left',
      sorter: fieldConfig.sortable,
      filterable: fieldConfig.filterable,
      fixed: (fieldConfig.key === 'factoryName' || fieldConfig.key === 'productName') ? 'left' : undefined,
      fieldConfig
    }));
  }, [visibleColumns]);

  // 处理搜索历史
  const handleSearchHistoryAdd = (searchText: string) => {
    if (!searchHistory.includes(searchText)) {
      setSearchHistory(prev => [searchText, ...prev.slice(0, 9)]); // 保留最近10条
    }
  };

  // 处理表格变化
  const handleTableChange = (paginationConfig: any, tableFilters: any, tableSorter: any) => {
    // 更新分页
    if (paginationConfig.current !== pagination.page || paginationConfig.pageSize !== pagination.limit) {
      setPage(paginationConfig.current, paginationConfig.pageSize);
    }

    // 更新排序
    if (tableSorter.field) {
      const sortConditions = FieldMappingUtils.buildSortConditions(tableSorter);
      setSort(sortConditions);
    }

    // 更新筛选
    const filterConditions = FieldMappingUtils.buildFilterConditions(tableFilters);
    setFilters(filterConditions);
  };

  // 导出菜单
  const exportMenu = (
    <Menu>
      <Menu.Item key="csv" icon={<FileTextOutlined />} onClick={() => exportData('csv')}>
        导出 CSV
      </Menu.Item>
      <Menu.Item key="excel" icon={<FileExcelOutlined />} onClick={() => exportData('excel')}>
        导出 Excel
      </Menu.Item>
      <Menu.Item key="json" icon={<FilePdfOutlined />} onClick={() => exportData('json')}>
        导出 JSON
      </Menu.Item>
    </Menu>
  );

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[], newSelectedRows: any[]) => {
      setSelectedRows(newSelectedRows, newSelectedRowKeys);
    },
    onSelectAll: (selected: boolean, selectedRows: any[], changeRows: any[]) => {
      console.log('Select all:', selected, selectedRows, changeRows);
    },
  };

  // 分页配置
  const paginationConfig = {
    current: pagination.page,
    pageSize: pagination.limit,
    total: pagination.total,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total: number, range: [number, number]) =>
      `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
    onChange: (page: number, pageSize: number) => setPage(page, pageSize),
    onShowSizeChange: (current: number, size: number) => setPage(current, size)
  };

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>数据浏览</Title>
            <Tag color="blue">传统表格模式</Tag>
            {systemInfo?.system_status?.database_connected && (
              <Tag color="green" icon={<InfoCircleOutlined />}>
                数据库已连接
              </Tag>
            )}
          </Space>
        }
        extra={
          <Space>
            <Tooltip title="刷新数据">
              <Button
                icon={<ReloadOutlined />}
                onClick={refresh}
                loading={loading}
              />
            </Tooltip>
            <Tooltip title="列配置">
              <Button
                icon={<SettingOutlined />}
                onClick={() => setColumnSelectorVisible(true)}
              />
            </Tooltip>
            <Dropdown overlay={exportMenu} trigger={['click']}>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
              >
                导出
              </Button>
            </Dropdown>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        {/* 搜索栏 */}
        <SearchBar
          onSearch={setSearch}
          onFilter={setFilters}
          onClear={clearFilters}
          loading={loading}
          searchHistory={searchHistory}
          onSearchHistoryAdd={handleSearchHistoryAdd}
        />

        {/* 选择信息 */}
        {selectedRowKeys.length > 0 && (
          <div style={{ marginBottom: 16, padding: 12, background: '#f0f2f5', borderRadius: 6 }}>
            <Space>
              <Text type="secondary">
                已选择 {selectedRowKeys.length} 条记录
              </Text>
              <Button size="small" onClick={clearSelection}>
                清除选择
              </Button>
            </Space>
          </div>
        )}

        {/* 数据表格 */}
        <DataTable
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={paginationConfig}
          rowSelection={rowSelection}
          onChange={handleTableChange}
          onColumnConfigChange={setVisibleColumns}
          density={tableDensity}
          onDensityChange={setTableDensity}
          summary={() => (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <Space>
                <Text type="secondary">
                  共 {pagination.total} 条记录
                </Text>
                <Text type="secondary">|</Text>
                <Text type="secondary">
                  当前页显示 {data.length} 条
                </Text>
                {error && (
                  <>
                    <Text type="secondary">|</Text>
                    <Text type="danger">{error}</Text>
                  </>
                )}
              </Space>
            </div>
          )}
        />

        {/* 列配置弹窗 */}
        <ColumnSelector
          visible={columnSelectorVisible}
          onClose={() => setColumnSelectorVisible(false)}
          visibleColumns={visibleColumns}
          onVisibleColumnsChange={setVisibleColumns}
          onSave={(columns) => {
            // 这里可以保存到本地存储或后端
            localStorage.setItem('browse_visible_columns', JSON.stringify(columns));
          }}
        />
      </Card>

      {/* 页面底部信息 */}
      <Card size="small" style={{ marginTop: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">数据浏览模式</Text>
              <Text type="secondary">|</Text>
              <Text type="secondary">支持筛选、排序、搜索、导出</Text>
              <Text type="secondary">|</Text>
              <Text type="secondary">显示 {visibleColumns.length} 个字段</Text>
            </Space>
          </Col>
          <Col>
            <Space>
              <Text type="secondary">
                最后更新: {new Date().toLocaleString('zh-CN')}
              </Text>
            </Space>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default BrowsePage;
